import { channelValueOf, getDeliveryChannel } from '@/features/deliveries';
import type { Delivery, DeliveryDetail } from '@/services/db/delivery/entity';
import {
  deliveryStateValue,
  getDeliveryState,
  getDeviceStatuses,
  getDisplayState,
  getErrorMessage,
  hasDeliveryError,
  metaValue,
} from '@/services/db/delivery/utils';
import { channelFormatterType, formatError } from '@/utils/format-error';
import { isEmptyString } from '@/utils/string';

/**
 * The delivery-detail alert's mutually exclusive states, in priority order — a
 * scheduled transactional send, a ready-to-send draft, a bounce/suppression/spam
 * reason (with the suppression-list link for email), a draft/failed error list,
 * or a single delivery error. `paused` wins over those for a ready delivery whose
 * newsletter is paused; `none` hides the alert (a successfully-sent message, a
 * push with device statuses, a holdout, or a state with no message to surface).
 */
export type DeliveryAlertState =
  | { kind: 'none' }
  | { kind: 'paused' }
  | { kind: 'scheduled'; sendAt: number }
  | { kind: 'ready' }
  | { kind: 'reason'; deliveryState: string; message: string; suppressionEmail: string | null }
  | { kind: 'errorList'; title: string; messages: string[] }
  | { kind: 'error'; message: string };

const trailingPeriod = /\.$/;

/** Format a raw delivery error/reason, then strip its trailing period so the caller can re-add one. */
function formatReason(delivery: Delivery, raw: string): string {
  const formatted = formatError(raw.trim(), { type: channelFormatterType(delivery.action_type) });
  return formatted.replace(trailingPeriod, '');
}

/** The channel short name backing the error-list title and the Fix button ("email", "webhook", …). */
export function deliveryChannelShortName(delivery: Delivery): string {
  return getDeliveryChannel(channelValueOf(delivery.action_type))?.shortName ?? '';
}

/** Whether the delivery has a related action (drives the error-list "Fix" button and title). */
export function hasDeliveryAction(delivery: Delivery): boolean {
  return delivery.action_id != null;
}

/** Whether the delivery has a related customer (drives the error-list "Add data" button). */
export function hasDeliveryCustomer(delivery: Delivery): boolean {
  return !isEmptyString(delivery.customer_id);
}

/**
 * The alert is hidden for holdouts, push deliveries whose per-device statuses are
 * shown separately, canceled deliveries (which will never send), and
 * successfully-sent messages. A bounce/suppression/spam stays visible only when
 * it has a reason or error to surface; without one, an already-sent bounce is
 * hidden like any other sent message rather than falling through to a misleading
 * "ready to send" banner.
 */
function isAlertHidden(delivery: Delivery, hasError: boolean): boolean {
  const state = getDeliveryState(delivery);
  if (state === 'holdout' || state === 'canceled') {
    return true;
  }
  if (channelValueOf(delivery.action_type) === 'push' && getDeviceStatuses(delivery).length > 0) {
    return true;
  }
  if ((state === 'suppressed' || state === 'bounced' || state === 'spammed') && hasError) {
    return false;
  }
  return deliveryStateValue(delivery, 'sent') > 0;
}

/** The error-list banner title, keyed on whether the delivery is a draft and whether it has an action. */
function errorListTitle(delivery: Delivery): string {
  if (getDeliveryState(delivery) === 'drafted') {
    return 'This draft has the following errors';
  }
  if (hasDeliveryAction(delivery)) {
    return `This ${deliveryChannelShortName(delivery)} failed to send`;
  }
  return 'This delivery failed to send';
}

/**
 * Resolve the delivery-detail alert to a single state. The hidden guard runs
 * first, so a successfully-sent (or holdout / push-with-device-statuses)
 * delivery never surfaces a banner even for a paused newsletter.
 * `newsletterIsPaused` comes from the (optional) parent newsletter; when absent
 * the paused banner never shows.
 */
export function resolveDeliveryAlertState(
  delivery: Delivery,
  detail: DeliveryDetail,
  { newsletterIsPaused = false }: { newsletterIsPaused?: boolean } = {},
): DeliveryAlertState {
  const hasError = hasDeliveryError(delivery, detail);
  if (isAlertHidden(delivery, hasError)) {
    return { kind: 'none' };
  }

  const isReadyToSend = !hasError;

  if (newsletterIsPaused && isReadyToSend) {
    return { kind: 'paused' };
  }

  const sendAt = Number(metaValue(delivery, 'send_at'));
  if (
    isReadyToSend &&
    delivery.transactional_message_id != null &&
    getDisplayState(delivery) === 'queued' &&
    Number.isFinite(sendAt) &&
    sendAt > 0
  ) {
    return { kind: 'scheduled', sendAt };
  }

  if (isReadyToSend) {
    return { kind: 'ready' };
  }

  const state = getDeliveryState(delivery);
  const reason = metaValue(delivery, 'reason');
  if ((state === 'bounced' || state === 'suppressed' || state === 'spammed') && !isEmptyString(reason)) {
    const isEmail = channelValueOf(delivery.action_type) === 'email';
    return {
      kind: 'reason',
      deliveryState: state,
      message: formatReason(delivery, reason ?? ''),
      suppressionEmail: isEmail ? delivery.recipient : null,
    };
  }

  const errorMessages = detail.error_messages ?? [];
  if (errorMessages.length > 0) {
    return {
      kind: 'errorList',
      title: errorListTitle(delivery),
      messages: errorMessages.map((message) =>
        formatError(String(message).trim(), { type: channelFormatterType(delivery.action_type) }),
      ),
    };
  }

  const errorMessage = getErrorMessage(delivery);
  if (!isEmptyString(errorMessage)) {
    return { kind: 'error', message: formatReason(delivery, errorMessage) };
  }

  return { kind: 'none' };
}
